import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {forkJoin, of, timer} from "rxjs";
import {catchError} from "rxjs/operators";

import {APP_VERSION, expandCollapse, INAPPBROWSER_CLOSE_BUTTON_CAPTION, INAPPBROWSER_TOOLBAR_COLOR, License} from "../shared/defines/client.const";
import {environment} from "../../environments/environment";
import {CordovaService} from "../shared/services/cordova/cordova.service";

type LICENSE_TYPE = 'web' | 'app' | 'ios' | 'android';
const listLicenseType: LICENSE_TYPE[] = ['web', 'app', 'ios', 'android'];

const SCROLL_DELAY = 400; // ms

function GetLicensePath(type: string): string {
  return `assets/license/${type}/licenses.json`;
}

function funcCmpLicense(a: License, b: License): number {
  const nameVersionA = a.name + a.version;
  const nameVersionB = b.name + b.version;
  return nameVersionA.localeCompare(nameVersionB);
}


// 재귀적으로 'setting-box' 클래스를 포함하는 부모 요소를 찾는 함수 정의
function findParentWithClass(element: HTMLElement, className: string): HTMLElement | null
{
  if (!element) return null;
  // 현재 요소에 className이 포함되어 있는지 확인
  if (element.classList.contains(className)) {
    return element;
  }
  // 부모 요소가 없으면 null 반환
  if (!element.parentElement) return null;
  // 부모 요소에 대해 재귀 호출
  return findParentWithClass(element.parentElement, className);
}

@Component({
  selector: 'app-open-source',
  templateUrl: './open-source.component.html',
  styleUrls: ['./open-source.component.scss'],
  animations: [expandCollapse],
})
export class OpenSourceComponent implements OnInit {
  @ViewChild('container', {static: true}) container: ElementRef;
  @ViewChild('header', {static: true}) header: ElementRef;

  public readonly versionTxt = APP_VERSION + (environment.text && environment.text.length > 0 ? ' (' + environment.text + ')' : '');

  listLicense: License[] = [];  // 스크립트를 통해서 자동으로 만들어진 라이선스 목록
  listLicenses: Map<LICENSE_TYPE, License> = new Map<LICENSE_TYPE, License>();
  listLicenseManual: Array<License> = [
    {
      name: 'Noto Sans Korea font',
      licenses: 'SIL Open Font License',
      url: 'https://fonts.google.com/noto/specimen/Noto+Sans+KR/about',
      licenseUrl: 'https://scripts.sil.org/cms/scripts/page.php?site_id=nrsi&id=OFL',
    },
    // {
    //   name: 'Hanazono font',
    //   publisher: 'GlyphWiki Project',
    //   licenses: 'Open Font License, SIL Open Font License 1.1',
    //   licenseFile: 'hanazono-LICENSE.txt',
    //   url: 'https://glyphwiki.org/hanazono/',
    //   repository: 'https://github.com/VdustR/hanamin',
    // },
    {
      name: 'Jigmo(字雲) font',
      publisher: '上地宏一',
      email: 'kamichi@ic.daito.ac.jp',
      licenses: 'CC0 1.0 Universal',
      licenseFile: 'jigmo-LICENSE.txt',
      url: 'https://kamichikoichi.github.io/jigmo/',
      repository: 'https://github.com/kamichikoichi/jigmo',
    },
    {
      name: 'SPICE',
      licenses: 'CC BY-NC 4.0',
      licenseUrl: 'https://creativecommons.org/licenses/by-nc/4.0/',
      url: 'https://www.kaggle.com/models/google/spice/tensorFlow1/spice',
    }
  ];  // 수동으로 만든 라이선스 목록

  currentLicense: License = null;

  constructor(
    private http: HttpClient,
    private cordovaSvc: CordovaService,
  ) {
    const requests = listLicenseType.map((type: LICENSE_TYPE) => {
      const filePath = GetLicensePath(type);
      return this.http.get<License[]>(filePath).pipe(
        catchError(error => {
          console.error('Failed to load licenses.json:', filePath, error);
          this.listLicenses[type] = [];
          return of([]); // 오류 발생 시 빈 배열 반환
        })
      );
    });

    forkJoin(requests).subscribe(results => {
      results.forEach((data: License[], index) => {
        const type = listLicenseType[index];
        this.listLicenses[type] = data.map((license: License) => {
          if (license.licenseFile) {
            license.licenseFile = `${type}/${license.licenseFile}`; // 'web/'를 licenseFile 앞에 추가
          }
          return license;
        });
      });

      this.mergeLicenseList(); // 모든 요청이 완료되었을 때 실행
    });
  }

  ngOnInit(): void {
  }

  private mergeLicenseList(): void {
    let combinedList: License[] =  [...this.listLicenseManual, ...this.listLicenses['web']];

    if (!this.cordovaSvc.onCordova) {
      this.listLicense = combinedList.sort(funcCmpLicense);
      return;
    }

    // 모든 배열을 합친다.
    // const combinedList = Object.values(this.listLicenses).reduce((acc, currentValue) => {
    //   return [...acc, ...currentValue];
    // }, []);
    combinedList = combinedList.concat(this.listLicenses['app']);
    if (this.cordovaSvc.isIOS) {
      combinedList = combinedList.concat(this.listLicenses['ios']);
    }
    else if (this.cordovaSvc.isAndroid) {
      combinedList = combinedList.concat(this.listLicenses['android']);
      // console.warn('this.listLicenses[android]):', this.listLicenses['android'].map((license: License) => license.name).join(', '));
      // console.warn('listLicenses:', combinedList.map((license: License) => license.name).join(', '));
    }

    // 'name + version'을 기반으로 중복을 제거한다.
    const uniqueList = combinedList.filter((license, index, self) =>
        index === self.findIndex((t) => (
          t.name === license.name && t.version === license.version
        ))
    );

    // 'name + version'을 기준으로 정렬한다.
    uniqueList.sort(funcCmpLicense);

    // 중복이 제거되고 정렬된 배열을 listLicense에 할당한다.
    this.listLicense = uniqueList;

    // console.warn('listLicense:', this.listLicense);
  }

  onClickLicense($event: MouseEvent, license: License = null) {
    $event.stopPropagation();
    $event.preventDefault();
    console.warn('onClickLicense:', license);

    if (license?.licenseFile) {
      this.currentLicense = license;

      // 이벤트 대상의 부모를 찾는 과정에서 findParentWithClass 함수를 활용
      const elToScroll = findParentWithClass($event.target as HTMLElement, 'setting-box');

      // console.warn('elToScroll:', elToScroll, this.container?.nativeElement, elToScroll?.offsetTop,
      //   this.container?.nativeElement.scrollTop, this.header?.nativeElement.offsetHeight);

      if (elToScroll) {
        timer(SCROLL_DELAY).subscribe(() => {
          this.container?.nativeElement.scrollTo({
            top: elToScroll.offsetTop - this.header?.nativeElement.offsetHeight,
            behavior: 'smooth',
          });
        });
      }
    } else {
      this.currentLicense = null;

      // 이벤트 대상의 부모를 찾는 과정에서 findParentWithClass 함수를 활용
      const elToScroll = findParentWithClass($event.target as HTMLElement, 'setting-box');

      if (elToScroll) {
        timer(SCROLL_DELAY).subscribe(() => {
          this.container?.nativeElement.scrollTo({
            top: elToScroll.offsetTop - this.header?.nativeElement.offsetHeight,
            behavior: 'smooth',
          });
        });
      }
    }
  }

  onCloseLicense() {
    this.currentLicense = null;
  }

  onClickUrl($event: MouseEvent, url: string) {
    $event.stopPropagation();
    $event.preventDefault();
    console.warn('onClickUrl:', url);

    const targetUrl: string = url.startsWith('http://') || url.startsWith('https://') ? url : 'http://' + url;
    if (this.cordovaSvc.onCordova) {
      const options = [
        'location=no',
        'fullscreen=yes',
        'toolbartranslucent=no',
        `toolbarcolor=${INAPPBROWSER_TOOLBAR_COLOR}`,
        `closebuttoncaption=${INAPPBROWSER_CLOSE_BUTTON_CAPTION}`,
        'hardwareback=yes',
        'hidenavigationbuttons=yes',
      ].join(',');

      this.cordovaSvc.cordova.InAppBrowser.open(targetUrl, '_blank', options);
    } else {
      window.open(targetUrl, '_system');
    }
  }
}
